import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

interface VideoConsentProps {
  onAccept: () => void;
}

const SECTION_KEYS = ['overview', 'recording', 'privacy', 'ai', 'eeo', 'accommodation'] as const;

const CERTIFICATION_ITEMS = ['read', 'consent', 'storage', 'accuracy', 'voluntary'] as const;

export function VideoConsent({ onAccept }: VideoConsentProps) {
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleContinue = useCallback(() => {
    if (!isChecked) {
      setShowError(true);
      return;
    }
    onAccept();
  }, [isChecked, onAccept]);

  const handleCheckboxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(e.target.checked);
    if (e.target.checked) {
      setShowError(false);
    }
  }, []);

  return (
    <div className='space-y-6'>
      <div>
        <div className='flex items-center gap-3 mb-2'>
          <h2 className='text-xl font-bold text-gray-900'>{t('video.consent.title')}</h2>
          <span className='text-xs font-medium text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full'>
            {t('video.consent.version')}
          </span>
        </div>
      </div>

      {/* Consent sections */}
      <div className='bg-gray-50 border rounded-lg p-4 md:p-6 max-h-[60vh] overflow-y-auto space-y-6 text-sm text-gray-700 leading-relaxed'>
        {/* Sections 1-6 */}
        {SECTION_KEYS.map((key) => (
          <div key={key}>
            <h3 className='text-sm font-semibold text-gray-900 mb-2'>{t(`video.consent.sections.${key}.title`)}</h3>
            <p>{t(`video.consent.sections.${key}.text`)}</p>
          </div>
        ))}

        {/* Section 7: Certification */}
        <div>
          <h3 className='text-sm font-semibold text-gray-900 mb-2'>
            {t('video.consent.sections.certification.title')}
          </h3>
          <p className='mb-3'>{t('video.consent.sections.certification.intro')}</p>
          <ul className='list-disc pl-5 space-y-1.5'>
            {CERTIFICATION_ITEMS.map((item) => (
              <li key={item}>{t(`video.consent.sections.certification.items.${item}`)}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Checkbox */}
      <div className={cn('p-4 rounded-md border', showError ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white')}>
        <label className='flex items-start gap-3 cursor-pointer'>
          <input
            type='checkbox'
            checked={isChecked}
            onChange={handleCheckboxChange}
            className='mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary'
          />
          <span className='text-sm font-medium text-gray-700'>{t('video.consent.checkboxLabel')}</span>
        </label>
        {showError ? <p className='text-xs text-red-600 mt-2 ml-7'>{t('video.consent.error')}</p> : null}
      </div>

      {/* Decline text */}
      <p className='text-xs text-gray-500 text-center'>{t('video.consent.declineText')}</p>

      {/* Continue button */}
      <div className='flex justify-end'>
        <button
          type='button'
          onClick={handleContinue}
          className={cn(
            'w-full md:w-auto px-6 py-2.5 rounded font-medium text-sm transition-colors',
            isChecked ? 'bg-primary text-white hover:bg-primary-dark' : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          )}>
          {t('video.consent.continue')}
        </button>
      </div>
    </div>
  );
}
